import { Document } from "model";

/**
 * @class OSGStatementSearchItem
 * @classdesc Class definition for the osg statement search item
 * @param {OBJECT} values - the values used to set the properties of the item
 * @example const item = OSGStatementSearchItem.init({});
 * 
 * @property {STRING} _account - The id of the selected account or "all" if the search should show all accounts
 * @property {ARRAY} _filtered - Array of document items used by the table
 */
export default class OSGStatementSearchItem {
    _account;
    _filtered;

    constructor({ account, startDate, endDate, document, category, filtered }) {
        this.account = account ?? "all";
        this.filtered = filtered ?? Document.sortDateAscending(Document.initAll());
    }

    /**
     * @function account
     * @instance
     * @param {STRING} value - The value to be set to the account property
     * @description The setter for the account property
     * @memberof OSGStatementSearchItem
     */
    set account(value) { this._account = value; }
    /**
     * @function filtered
     * @instance
     * @param {ARRAY} value - The value to be set for the filtered property
     * @memberof OSGStatementSearchItem
     */
    set filtered(value) { this._filtered = value; }

    /**
     * @function account
     * @instance
     * @returns {STRING}
     * @description Getter for the account property
     * @memberof OSGStatementSearchItem
     */
    get account() { return this._account; }
    /**
     * @function filtered
     * @instance
     * @returns {ARRAY}
     * @description Getter for the fitlered property
     * @memberof OSGStatementSearchItem
     */
    get filtered() { return this._filtered; }

    /**
     * @function record
     * @instance
     * @returns {OBJECT}
     * @description Getter to retrieve all the values from the class as an object
     * @memberof OSGStatementSearchItem
     */
    get record() {
        return {
            account: this.account,
            filtered: this.filtered
        };
    }

    /**
     * @function reset
     * @instance
     * @returns {VOID}
     * @description Function to reset the values
     * @memberof OSGStatementSearchItem
     */
    reset() {
        this.account = "all";
        this.filtered = Document.sortDateAscending(Document.initAll());
        return this;
    }

    /**
     * @function filter
     * @instance
     * @returns {VOID}
     * @description Function to set the filtered array based on the values set
     * @memberof OSGStatementSearchItem
     */
    filter() {
        this.filtered = Object.keys(this.record).reduce((result, key) => {
            switch(key) {
                case "account":
                    return this.record[key] !== "all"
                        ? result.filter(document => document.account.id === this.record[key], [])
                        : result;
                default:
                    return result;
            }
        }, Document.sortDateAscending(Document.initAll()));

        return this;
    }

    /**
     * @function init
     * @static
     * @param {OBJECT} values - Values to be passed to the statement search item
     * @returns {OSGStatementSearchItem}
     * @description Function to return an initalized instance
     * @memberof OSGStatementSearchItem
     */
    static init(values) { return new OSGStatementSearchItem(values ?? {}); }
}